import { getAgentTimezone } from 'v2/api-console/shared/utils/date-utils'
import { REGIONS } from 'v2/configs/constants'
import { FlowTypes, type PhloType } from 'v2/types'
import { getLocalAuth } from 'v2/utils'
import dayjs from 'v2/utils/dayjs'
import type { FlowRunEvent, Metrics } from '../run-info/types'
import { FlowNodeTypes, FlowRunStatus } from '../types'
import { isAiAgentV2Type } from '../utils/ai-agent-v29'

export const getStatusVariant = (status: FlowRunStatus) => {
  switch (status) {
    case FlowRunStatus.COMPLETED:
      return 'success'
    case FlowRunStatus.FAILED:
    case FlowRunStatus.EXPIRED:
      return 'destructive'
    case FlowRunStatus.IN_PROGRESS:
      return 'progress'
    case FlowRunStatus.QUEUED:
      return 'warning'
    default:
      return 'default'
  }
}

export const formatDateTime = (timestamp: number | null) => {
  if (timestamp === null || timestamp === undefined) return '-'
  return dayjs(timestamp * 1000)
    .tz(getAgentTimezone())
    .format('MMM D, YYYY h:mm A')
}

const STT_LATENCY_OFFSET = {
  DEFAULT: 250, // Default offset for US region
  IN: 300, // Offset for India region
} as const

// Fields that require latency offset adjustment
const FIELDS_REQUIRING_OFFSET: Array<keyof Metrics> = ['pipeline_latency', 'EOU_transcript_latency']

export const getValueForMetricCell = (
  field: keyof Metrics,
  fieldValue: Metrics[keyof Metrics],
  metrics: Metrics,
) => {
  if (typeof fieldValue !== 'number') return null
  let value = fieldValue

  const isSttLatencyPrecomputed = metrics.stt_latency_precomputed ?? false
  if (!isSttLatencyPrecomputed) {
    const { region } = getLocalAuth()

    const latencyOffset = region === REGIONS.IN ? STT_LATENCY_OFFSET.IN : STT_LATENCY_OFFSET.DEFAULT

    if (FIELDS_REQUIRING_OFFSET.includes(field)) {
      value += latencyOffset
    }
  }

  return value
}

export const getFirstEventTimestamp = (
  currentFlowType: PhloType | null,
  events: FlowRunEvent[],
) => {
  let nodeType = FlowNodeTypes.start,
    chosenPath = ''

  if (currentFlowType === FlowTypes.api_request) {
    // For API request flow/ outbound call, we need to find the first event that has call answered.
    nodeType = FlowNodeTypes.ai_agent_v2
    chosenPath = 'answered'
  }

  const firstEvent =
    events.find(
      (e) =>
        e.event_name === 'NODE_EXECUTED' &&
        (e.current_node_type === nodeType ||
          (nodeType === FlowNodeTypes.ai_agent_v2 && isAiAgentV2Type(e.current_node_type))) &&
        e.chosen_path === chosenPath,
    ) || events[0]

  return dayjs.utc(firstEvent.timestamp).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
}
